import React, { useEffect, useMemo, useState } from "react";

const SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const DAILY_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

const TRADING_DAYS = 252;
const DEFAULT_RISK_FREE_RATE = 0.045;

const DEFAULT_FORM = {
  ticker: "AAPL",
  strikeDate: "",
  strikePrice: "",
  transaction: "Buy",
  optionType: "Call",
  currentPrice: "",
};

function normalizeTicker(value) {
  return String(value || "").trim().toUpperCase();
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,%]/g, "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < String(line || "").length; i++) {
    const ch = String(line || "")[i];
    const next = String(line || "")[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function normalizeScreenerPayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function formatMoney(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(digits)}`;
}

function formatPercent(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function formatNumber(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

function daysBetweenToday(dateString) {
  if (!dateString) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const expiry = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(expiry.getTime())) return null;

  const diffMs = expiry.getTime() - today.getTime();
  return Math.ceil(diffMs / 86400000);
}

function erf(x) {
  const sign = x >= 0 ? 1 : -1;
  const ax = Math.abs(x);

  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const t = 1 / (1 + p * ax);
  const y =
    1 -
    (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) *
      t *
      Math.exp(-ax * ax);

  return sign * y;
}

function normalCdf(x) {
  return 0.5 * (1 + erf(x / Math.sqrt(2)));
}

function normalPdf(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

function blackScholes({
  optionType,
  stockPrice,
  strikePrice,
  timeYears,
  riskFreeRate,
  volatility,
}) {
  const S = Number(stockPrice);
  const K = Number(strikePrice);
  const T = Number(timeYears);
  const r = Number(riskFreeRate);
  const sigma = Number(volatility);

  if (
    !Number.isFinite(S) ||
    !Number.isFinite(K) ||
    !Number.isFinite(T) ||
    !Number.isFinite(r) ||
    !Number.isFinite(sigma) ||
    S <= 0 ||
    K <= 0 ||
    T <= 0 ||
    sigma <= 0
  ) {
    return null;
  }

  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;

  const call = S * normalCdf(d1) - K * Math.exp(-r * T) * normalCdf(d2);
  const put = K * Math.exp(-r * T) * normalCdf(-d2) - S * normalCdf(-d1);

  const price = optionType === "Call" ? call : put;

  const delta = optionType === "Call" ? normalCdf(d1) : normalCdf(d1) - 1;
  const gamma = normalPdf(d1) / (S * sigma * sqrtT);
  const vega = S * normalPdf(d1) * sqrtT / 100;
  const thetaCall =
    (-(S * normalPdf(d1) * sigma) / (2 * sqrtT) -
      r * K * Math.exp(-r * T) * normalCdf(d2)) /
    365;
  const thetaPut =
    (-(S * normalPdf(d1) * sigma) / (2 * sqrtT) +
      r * K * Math.exp(-r * T) * normalCdf(-d2)) /
    365;
  const theta = optionType === "Call" ? thetaCall : thetaPut;

  const rhoCall = (K * T * Math.exp(-r * T) * normalCdf(d2)) / 100;
  const rhoPut = (-K * T * Math.exp(-r * T) * normalCdf(-d2)) / 100;
  const rho = optionType === "Call" ? rhoCall : rhoPut;

  return {
    price,
    d1,
    d2,
    delta,
    gamma,
    vega,
    theta,
    rho,
  };
}

function stdev(values) {
  const clean = values.map(Number).filter(Number.isFinite);
  if (clean.length < 2) return null;

  const mean = clean.reduce((sum, value) => sum + value, 0) / clean.length;
  const variance =
    clean.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) /
    (clean.length - 1);

  return Math.sqrt(variance);
}

function annualizedVolatility(dailyReturns) {
  const sd = stdev(dailyReturns);
  if (!Number.isFinite(sd)) return null;
  return sd * Math.sqrt(TRADING_DAYS);
}

function parseDailyReturns(csvText) {
  const lines = String(csvText || "")
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (lines.length < 2) return { tickerReturns: {}, latestDate: null };

  const headers = parseCsvLine(lines[0]).map((h) => String(h || "").trim());
  const tickerReturns = {};
  const tickers = headers.slice(1).map(normalizeTicker).filter(Boolean);

  tickers.forEach((ticker) => {
    tickerReturns[ticker] = [];
  });

  let latestDate = null;

  for (let r = 1; r < lines.length; r++) {
    const cols = parseCsvLine(lines[r]);
    latestDate = cols[0] || latestDate;

    for (let c = 1; c < headers.length; c++) {
      const ticker = normalizeTicker(headers[c]);
      if (!ticker) continue;

      const raw = Number(cols[c]);
      if (Number.isFinite(raw)) {
        tickerReturns[ticker].push(raw / 100);
      }
    }
  }

  return { tickerReturns, latestDate };
}

function getMoneynessLabel(stockPrice, strikePrice, type) {
  const S = Number(stockPrice);
  const K = Number(strikePrice);
  if (!Number.isFinite(S) || !Number.isFinite(K) || S <= 0 || K <= 0) return "—";

  const pct = S / K - 1;

  if (type === "Call") {
    if (S > K) return `In the money by ${(pct * 100).toFixed(1)}%`;
    if (S < K) return `Out of the money by ${Math.abs(pct * 100).toFixed(1)}%`;
    return "At the money";
  }

  if (S < K) return `In the money by ${Math.abs(pct * 100).toFixed(1)}%`;
  if (S > K) return `Out of the money by ${(pct * 100).toFixed(1)}%`;
  return "At the money";
}

function getOptionVerdict(transaction, fairValue, marketPrice) {
  const fv = Number(fairValue);
  const mp = Number(marketPrice);

  if (!Number.isFinite(fv) || !Number.isFinite(mp) || mp <= 0) {
    return "Enter a real market option price later to compare against fair value.";
  }

  const diff = fv / mp - 1;

  if (transaction === "Buy") {
    if (diff > 0.15) return "Looks cheap versus model fair value.";
    if (diff < -0.15) return "Looks expensive versus model fair value.";
    return "Looks roughly fair versus model value.";
  }

  if (diff > 0.15) return "Selling may be underpaid versus model fair value.";
  if (diff < -0.15) return "Selling may be attractive versus model fair value.";
  return "Selling looks roughly fair versus model value.";
}

export default function OptionPricerPage() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [optionMarketPrice, setOptionMarketPrice] = useState("");
  const [riskFreeRate, setRiskFreeRate] = useState(DEFAULT_RISK_FREE_RATE);
  const [stockRows, setStockRows] = useState([]);
  const [tickerReturns, setTickerReturns] = useState({});
  const [returnsDate, setReturnsDate] = useState(null);
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  function updateForm(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  useEffect(() => {
    let alive = true;

    async function loadData() {
      try {
        setLoading(true);
        setError("");

        const [screenerRes, returnsRes] = await Promise.all([
          fetch(SCREENER_URL, { cache: "no-store" }),
          fetch(DAILY_RETURNS_URL, { cache: "no-store" }),
        ]);

        const [screenerText, returnsText] = await Promise.all([
          screenerRes.text(),
          returnsRes.text(),
        ]);

        if (!screenerRes.ok || screenerText.trim().startsWith("<")) {
          throw new Error(
            `Could not load stock_screener.json. First 120 chars: ${screenerText.slice(
              0,
              120
            )}`
          );
        }

        if (!returnsRes.ok || returnsText.trim().startsWith("<")) {
          throw new Error(
            `Could not load daily_returns.csv. First 120 chars: ${returnsText.slice(
              0,
              120
            )}`
          );
        }

        const screenerJson = JSON.parse(screenerText);
        const returns = parseDailyReturns(returnsText);

        if (alive) {
          setStockRows(normalizeScreenerPayload(screenerJson));
          setAsOf(screenerJson?.as_of || null);
          setTickerReturns(returns.tickerReturns || {});
          setReturnsDate(returns.latestDate || null);
        }
      } catch (err) {
        console.error(err);
        if (alive) setError(err?.message || "Failed to load option pricing data.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadData();

    return () => {
      alive = false;
    };
  }, []);

  const stockByTicker = useMemo(() => {
    const map = new Map();

    for (const row of stockRows) {
      const ticker = normalizeTicker(row?.ticker);
      if (!ticker) continue;
      map.set(ticker, row);
    }

    return map;
  }, [stockRows]);

  const result = useMemo(() => {
    const ticker = normalizeTicker(form.ticker);
    const row = stockByTicker.get(ticker);

    const dbPrice = parseNumber(row?.price);
    const currentPrice = parseNumber(form.currentPrice) || dbPrice;
    const strikePrice = parseNumber(form.strikePrice);
    const daysToExpiry = daysBetweenToday(form.strikeDate);
    const timeYears =
      Number.isFinite(daysToExpiry) && daysToExpiry > 0 ? daysToExpiry / 365 : null;

    const returns = tickerReturns[ticker] || [];
    const trailingReturns = returns.slice(-252);
    const realizedVol = annualizedVolatility(trailingReturns);

    const beta = parseNumber(row?.beta);
    const fallbackVol =
      Number.isFinite(beta) && beta > 0
        ? Math.max(0.18, Math.min(1.1, 0.22 * beta))
        : 0.35;

    const baseVol = Number.isFinite(realizedVol) ? realizedVol : fallbackVol;

    const lowVol = Math.max(0.05, baseVol * 0.8);
    const highVol = Math.min(2.5, baseVol * 1.25);

    const low = blackScholes({
      optionType: form.optionType,
      stockPrice: currentPrice,
      strikePrice,
      timeYears,
      riskFreeRate,
      volatility: lowVol,
    });

    const base = blackScholes({
      optionType: form.optionType,
      stockPrice: currentPrice,
      strikePrice,
      timeYears,
      riskFreeRate,
      volatility: baseVol,
    });

    const high = blackScholes({
      optionType: form.optionType,
      stockPrice: currentPrice,
      strikePrice,
      timeYears,
      riskFreeRate,
      volatility: highVol,
    });

    const errors = [];
    if (!ticker) errors.push("Ticker is required.");
    if (!row) errors.push("Ticker was not found in stock_screener.json.");
    if (!Number.isFinite(currentPrice) || currentPrice <= 0) errors.push("Current price is missing.");
    if (!Number.isFinite(strikePrice) || strikePrice <= 0) errors.push("Strike price must be above 0.");
    if (!Number.isFinite(daysToExpiry) || daysToExpiry <= 0) errors.push("Strike date must be in the future.");

    return {
      ticker,
      row,
      currentPrice,
      dbPrice,
      strikePrice,
      daysToExpiry,
      timeYears,
      beta,
      realizedVol,
      fallbackVol,
      baseVol,
      lowVol,
      highVol,
      low,
      base,
      high,
      errors,
      moneyness: getMoneynessLabel(currentPrice, strikePrice, form.optionType),
    };
  }, [form, stockByTicker, tickerReturns, riskFreeRate]);

  const fairLow = result.low?.price;
  const fairBase = result.base?.price;
  const fairHigh = result.high?.price;
  const marketOptionPrice = parseNumber(optionMarketPrice);

  return (
    <div style={pageShell}>
      <section style={heroCard}>
        <div style={eyebrow}>Majorah Options Lab</div>
        <h1 style={title}>Call / Put Fair Value Range</h1>
        <p style={subtitle}>Model utilizes black scholes
          {asOf ? ` Screener as of ${asOf}.` : ""}
          {returnsDate ? ` Returns through ${returnsDate}.` : ""}
        </p>

        <div style={heroGrid}>
          <HeroStat label="Ticker" value={result.ticker || "—"} />
          <HeroStat label="Current Price" value={formatMoney(result.currentPrice)} />
          <HeroStat label="Base Volatility" value={formatPercent(result.baseVol)} />
          <HeroStat label="Days to Expiry" value={result.daysToExpiry || "—"} />
        </div>
      </section>

      {loading ? (
        <div style={messageCard}>Loading option model data...</div>
      ) : error ? (
        <div style={errorCard}>{error}</div>
      ) : (
        <>
          <section style={inputCard}>
            <div>
              <h2 style={sectionTitle}>Inputs</h2>
              <p style={sectionSubtitle}>
                Current price is optional. If blank, the model uses the last available price.
              </p>
            </div>

            <div style={inputGrid}>
              <Input label="Stock Ticker">
                <input
                  value={form.ticker}
                  onChange={(e) => updateForm("ticker", e.target.value.toUpperCase())}
                  placeholder="AAPL"
                  style={inputStyle}
                />
              </Input>

              <Input label="Strike Date">
                <input
                  value={form.strikeDate}
                  onChange={(e) => updateForm("strikeDate", e.target.value)}
                  type="date"
                  style={inputStyle}
                />
              </Input>

              <Input label="Strike Price">
                <input
                  value={form.strikePrice}
                  onChange={(e) => updateForm("strikePrice", e.target.value)}
                  placeholder="200"
                  type="number"
                  style={inputStyle}
                />
              </Input>

              <Input label="Transaction">
                <select
                  value={form.transaction}
                  onChange={(e) => updateForm("transaction", e.target.value)}
                  style={inputStyle}
                >
                  <option>Buy</option>
                  <option>Sell</option>
                </select>
              </Input>

              <Input label="Type">
                <select
                  value={form.optionType}
                  onChange={(e) => updateForm("optionType", e.target.value)}
                  style={inputStyle}
                >
                  <option>Call</option>
                  <option>Put</option>
                </select>
              </Input>

              <Input label="Current Price Optional">
                <input
                  value={form.currentPrice}
                  onChange={(e) => updateForm("currentPrice", e.target.value)}
                  placeholder={formatMoney(result.dbPrice)}
                  type="number"
                  style={inputStyle}
                />
              </Input>

              <Input label="Risk-Free Rate">
                <input
                  value={riskFreeRate}
                  onChange={(e) => setRiskFreeRate(Number(e.target.value))}
                  type="number"
                  step="0.001"
                  style={inputStyle}
                />
              </Input>

              <Input label="Option Market Price Optional">
                <input
                  value={optionMarketPrice}
                  onChange={(e) => setOptionMarketPrice(e.target.value)}
                  placeholder="Compare actual premium"
                  type="number"
                  style={inputStyle}
                />
              </Input>
            </div>
          </section>

          {result.errors.length ? (
            <section style={warningCard}>
              {result.errors.map((err) => (
                <div key={err}>• {err}</div>
              ))}
            </section>
          ) : (
            <>
              <section style={resultsGrid}>
                <ResultCard
                  label="Fair Value Low"
                  value={formatMoney(fairLow)}
                  sublabel={`Vol ${formatPercent(result.lowVol)}`}
                />
                <ResultCard
                  label="Fair Value Base"
                  value={formatMoney(fairBase)}
                  sublabel={`Vol ${formatPercent(result.baseVol)}`}
                  featured
                />
                <ResultCard
                  label="Fair Value High"
                  value={formatMoney(fairHigh)}
                  sublabel={`Vol ${formatPercent(result.highVol)}`}
                />
              </section>

              <section style={analysisCard}>
                <div>
                  <h2 style={sectionTitle}>Model Readout</h2>
                  <p style={sectionSubtitle}>
                    {form.transaction} {form.optionType} • Strike {formatMoney(result.strikePrice)} • {result.moneyness}
                  </p>
                </div>

                <div style={readoutGrid}>
                  <Readout label="Fair Range" value={`${formatMoney(fairLow)} – ${formatMoney(fairHigh)}`} />
                  <Readout label="Base Fair Value" value={formatMoney(fairBase)} />
                  <Readout label="Current Stock Price" value={formatMoney(result.currentPrice)} />
                  <Readout label="DB Price Used" value={formatMoney(result.dbPrice)} />
                  <Readout label="Beta" value={formatNumber(result.beta, 2)} />
                  <Readout label="Realized Vol" value={Number.isFinite(result.realizedVol) ? formatPercent(result.realizedVol) : "Fallback used"} />
                  <Readout label="Delta" value={formatNumber(result.base?.delta, 3)} />
                  <Readout label="Gamma" value={formatNumber(result.base?.gamma, 4)} />
                  <Readout label="Theta / Day" value={formatNumber(result.base?.theta, 4)} />
                  <Readout label="Vega / 1 Vol Pt" value={formatNumber(result.base?.vega, 4)} />
                  <Readout label="Rho / 1 Rate Pt" value={formatNumber(result.base?.rho, 4)} />
                  <Readout
                    label="Market Comparison"
                    value={getOptionVerdict(form.transaction, fairBase, marketOptionPrice)}
                    wide
                  />
                </div>
              </section>

              <section style={scenarioCard}>
                <h2 style={sectionTitle}>Scenario Table</h2>
                <div style={tableWrap}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Scenario</th>
                        <th style={thRight}>Volatility</th>
                        <th style={thRight}>Fair Value</th>
                        <th style={thRight}>Delta</th>
                        <th style={thRight}>Theta / Day</th>
                        <th style={thRight}>Vega</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Low", result.lowVol, result.low],
                        ["Base", result.baseVol, result.base],
                        ["High", result.highVol, result.high],
                      ].map(([label, vol, model], index) => (
                        <tr key={label} style={index % 2 === 0 ? rowEven : rowOdd}>
                          <td style={tdStyle}>{label}</td>
                          <td style={tdRight}>{formatPercent(vol)}</td>
                          <td style={tdRight}>{formatMoney(model?.price)}</td>
                          <td style={tdRight}>{formatNumber(model?.delta, 3)}</td>
                          <td style={tdRight}>{formatNumber(model?.theta, 4)}</td>
                          <td style={tdRight}>{formatNumber(model?.vega, 4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          <section style={noteCard}>
            <strong>Important:</strong> This estimates theoretical fair value from your available
            data. It does not include live option-chain implied volatility, bid/ask spread,
            dividends, early exercise effects, hard-to-borrow costs, or event-specific jumps.
            Treat the output as a model range, not a quote.
          </section>
        </>
      )}
    </div>
  );
}

function Input({ label, children }) {
  return (
    <label style={inputGroup}>
      <span style={inputLabel}>{label}</span>
      {children}
    </label>
  );
}

function HeroStat({ label, value }) {
  return (
    <div style={heroStatCard}>
      <div style={heroStatLabel}>{label}</div>
      <div style={heroStatValue}>{value}</div>
    </div>
  );
}

function ResultCard({ label, value, sublabel, featured = false }) {
  return (
    <div style={featured ? featuredResultCard : resultCard}>
      <div style={resultLabel}>{label}</div>
      <div style={resultValue}>{value}</div>
      <div style={resultSubLabel}>{sublabel}</div>
    </div>
  );
}

function Readout({ label, value, wide = false }) {
  return (
    <div style={{ ...readoutCard, gridColumn: wide ? "1 / -1" : undefined }}>
      <div style={readoutLabel}>{label}</div>
      <div style={readoutValue}>{value}</div>
    </div>
  );
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "22px 10px 34px",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1280,
  margin: "0 auto 18px",
  padding: 24,
  background:
    "radial-gradient(circle at 18% 10%, rgba(56,189,248,0.24), transparent 32%), linear-gradient(135deg, #0f172a 0%, #1e293b 58%, #334155 100%)",
  color: "white",
  borderRadius: 26,
  boxShadow: "0 24px 58px rgba(15,23,42,0.22)",
};

const eyebrow = {
  color: "#93c5fd",
  fontSize: 12,
  letterSpacing: "0.13em",
  textTransform: "uppercase",
  fontWeight: 900,
  marginBottom: 8,
};

const title = {
  margin: 0,
  fontSize: 36,
  lineHeight: 1,
  letterSpacing: "-0.06em",
};

const subtitle = {
  maxWidth: 940,
  marginTop: 12,
  color: "#cbd5e1",
  lineHeight: 1.65,
  fontSize: 14,
};

const heroGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 10,
  marginTop: 22,
};

const heroStatCard = {
  padding: 14,
  borderRadius: 18,
  background: "rgba(255,255,255,0.1)",
  border: "1px solid rgba(255,255,255,0.16)",
};

const heroStatLabel = {
  fontSize: 10,
  fontWeight: 850,
  color: "#cbd5e1",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const heroStatValue = {
  marginTop: 7,
  fontSize: 20,
  fontWeight: 950,
};

const inputCard = {
  maxWidth: 1280,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "rgba(255,255,255,0.92)",
  boxShadow: "0 14px 32px rgba(15,23,42,0.08)",
};

const sectionTitle = {
  margin: 0,
  fontSize: 18,
  letterSpacing: "-0.03em",
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: 12,
};

const inputGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  marginTop: 16,
};

const inputGroup = {
  display: "grid",
  gap: 6,
};

const inputLabel = {
  fontSize: 11,
  fontWeight: 850,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 11px",
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  background: "white",
  outline: "none",
  color: "#0f172a",
  fontWeight: 750,
};

const resultsGrid = {
  maxWidth: 1280,
  margin: "0 auto 18px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
};

const resultCard = {
  padding: 18,
  borderRadius: 22,
  background: "white",
  border: "1px solid #e2e8f0",
  boxShadow: "0 14px 34px rgba(15,23,42,0.08)",
};

const featuredResultCard = {
  ...resultCard,
  background: "linear-gradient(135deg, #ecfdf5 0%, #eff6ff 100%)",
  border: "1px solid #a7f3d0",
};

const resultLabel = {
  fontSize: 11,
  fontWeight: 900,
  color: "#64748b",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const resultValue = {
  marginTop: 8,
  fontSize: 34,
  fontWeight: 950,
  letterSpacing: "-0.06em",
  color: "#0f172a",
};

const resultSubLabel = {
  marginTop: 6,
  fontSize: 13,
  color: "#64748b",
  fontWeight: 800,
};

const analysisCard = {
  maxWidth: 1280,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 14px 32px rgba(15,23,42,0.08)",
};

const readoutGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  marginTop: 16,
};

const readoutCard = {
  padding: 13,
  borderRadius: 16,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
};

const readoutLabel = {
  fontSize: 10,
  color: "#64748b",
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const readoutValue = {
  marginTop: 6,
  fontSize: 15,
  color: "#0f172a",
  fontWeight: 900,
};

const scenarioCard = {
  maxWidth: 1280,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 14px 32px rgba(15,23,42,0.08)",
};

const tableWrap = {
  marginTop: 14,
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 16,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: 720,
};

const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 11,
  color: "#475569",
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
};

const thRight = {
  ...thStyle,
  textAlign: "right",
};

const tdStyle = {
  padding: "10px 12px",
  fontSize: 13,
  borderBottom: "1px solid #eef2f7",
  color: "#1e293b",
  whiteSpace: "nowrap",
};

const tdRight = {
  ...tdStyle,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const rowEven = { background: "#ffffff" };
const rowOdd = { background: "#fbfdff" };

const warningCard = {
  maxWidth: 1280,
  margin: "0 auto 18px",
  padding: 16,
  borderRadius: 18,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fed7aa",
  fontWeight: 800,
  lineHeight: 1.6,
};

const noteCard = {
  maxWidth: 1280,
  margin: "0 auto",
  padding: 16,
  borderRadius: 18,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
  lineHeight: 1.6,
  fontSize: 13,
};

const messageCard = {
  maxWidth: 1280,
  margin: "0 auto",
  padding: 18,
  borderRadius: 16,
  background: "white",
  border: "1px solid #e2e8f0",
  color: "#64748b",
};

const errorCard = {
  maxWidth: 1280,
  margin: "0 auto",
  padding: 16,
  borderRadius: 16,
  background: "#fff1f2",
  border: "1px solid #fecdd3",
  color: "#9f1239",
};
